import * as React from 'react';
import * as PIXI from 'pixi.js';
import * as filters from 'pixi-filters';

import styled from '../../theme';

import {
  IRenderProps,
  WhistleTrackRenderer,
  IWhistleTrack,
  WhistleTrackEntry,
} from '../../models/WhistleTrack';

import DancingMan1 from '../../assets/dancing-man-1.png';
import DancingMan2 from '../../assets/dancing-man-2.png';
import DancingManDrag from '../../assets/dancing-man-drag.png';
import SoundOn from '../../assets/soundon.svg';
import SoundOff from '../../assets/soundoff.svg';

let sprite: any;
let dance1Texture: PIXI.Texture;
let dance2Texture: PIXI.Texture;
let danceDragTexture: PIXI.Texture;

const spriteSize = 70;
const speed = 3.2;

interface IState {
  currentTrack: number;
  soundEnabled: boolean;
}

const SoundControl = styled.img`
  position: fixed;
  top: 20px;
  left: 20px;
  width: 30px;
  z-index: 100;
  cursor: pointer;
`;

class DancingManDesktop extends React.PureComponent<IRenderProps, IState> {
  container?: HTMLDivElement;
  app: PIXI.Application | null;
  spriteInterval: NodeJS.Timer;
  state: IState;
  spriteContainer: any;
  bubbles: PIXI.Container;

  constructor(props: IRenderProps) {
    super(props);

    this.state = {
      currentTrack: this.randomTrack(props.data),
      soundEnabled: true,
    };
  }

  componentDidMount() {
    const resolution = window.devicePixelRatio;
    this.app = new PIXI.Application({
      resolution,
      width: window.innerWidth,
      height: window.innerHeight,
      transparent: true,
      antialias: true,
      autoResize: true,
    });

    const app = this.app;
    const instance = this;

    app.renderer.view.style.position = 'fixed';
    app.renderer.view.style.display = 'block';
    this.container!.appendChild(app.view);
    PIXI.settings.FILTER_RESOLUTION = window.devicePixelRatio;
    const screen = app.screen;

    PIXI.loader
      .add(DancingMan1)
      .add(DancingMan2)
      .add(DancingManDrag)
      .load(setup);

    const spriteContainer: any = new PIXI.Container();
    this.spriteContainer = spriteContainer;
    spriteContainer.interactive = true;
    this.createBubbles(this.retrieveTrack(this.state.currentTrack));

    let prevVx = 0;
    let prevVy = 0;
    let dragging = false;
    enum wall {
      left,
      bottom,
      right,
      top,
    }
    let lastCollision = wall.bottom;
    let falling = false;
    let gravityInterval: NodeJS.Timer;
    let forwardDelta = 0;
    let mouseover = false;

    function setup() {
      dance1Texture = PIXI.loader.resources[DancingMan1].texture;
      dance2Texture = PIXI.loader.resources[DancingMan2].texture;
      danceDragTexture = PIXI.loader.resources[DancingManDrag].texture;

      sprite = new PIXI.Sprite(dance1Texture);
      spriteContainer.addChild(sprite);
      sprite.interactive = true;
      sprite.height = spriteSize;
      sprite.width = spriteSize;

      spriteContainer.x = 0;
      spriteContainer.y = screen.height - spriteSize + resolution;
      spriteContainer.vx = speed;
      spriteContainer.vy = 0;
      app.stage.addChild(spriteContainer);
      app.ticker.add(gameLoop);

      const initGravity = () => {
        const screenWidth = screen.width;
        const screenHeight = screen.height;

        let start = forwardDelta / 7;
        if (start >= 5) {
          start = 5;
        }
        spriteContainer.vy -= start;
        start /= 2;
        clearInterval(gravityInterval);
        setTimeout(() => {
          gravityInterval = setInterval(() => {
            if (spriteContainer.y + spriteSize + resolution >= screenHeight) {
              clearInterval(gravityInterval);
              spriteContainer.y = screenHeight - spriteSize + resolution;
              return;
            }

            if (spriteContainer.x + spriteSize + spriteSize / 8 > screenWidth) {
              spriteContainer.x = screenWidth - spriteSize;
              spriteContainer.vx = -speed * 3;
            }
            if (start > 0) {
              spriteContainer.vy -= start;
              start /= 2;
              if (start <= 4) {
                start = 0;
              }
            } else {
              spriteContainer.vy += 1.8;
            }
          }, 50);
        }, 50);
      };

      spriteContainer.mouseover = function() {
        mouseover = true;
        if (falling) {
          return;
        }
        app.renderer.view.style.zIndex = '10';

        if (spriteContainer.vx !== 0 || spriteContainer.vy !== 0) {
          prevVx = spriteContainer.vx;
          prevVy = spriteContainer.vy;
        }

        spriteContainer.vy = 0;
        spriteContainer.vx = 0;
        instance.bubbles.alpha = 1;
        spriteContainer.cursor = 'pointer';
      };

      spriteContainer.mouseout = function() {
        mouseover = false;
        if (falling) {
          return;
        }
        app.renderer.view.style.zIndex = '0';
        instance.bubbles.alpha = 0;
        if (!dragging) {
          spriteContainer.vy = prevVy;
          spriteContainer.vx = prevVx;
        }
      };

      spriteContainer.click = function() {
        setTimeout(() => {
          if (!dragging && !falling) {
            instance.setState({ currentTrack: instance.nextTrack() });
          }
        }, 100);
      };

      const beginDrag = function() {
        falling = true;
        sprite.texture = danceDragTexture;
        instance.bubbles.alpha = 0;
        lastCollision = wall.left;

        if (spriteContainer.vx !== 0 || spriteContainer.vy !== 0) {
          prevVx = spriteContainer.vx;
          prevVy = spriteContainer.vy;
        }
        spriteContainer.vy = 0;
        spriteContainer.vx = 0;
        app.renderer.view.style.zIndex = '10';

        spriteContainer.cursor = 'grabbing';
        spriteContainer.rotation = 0;
        instance.bubbles.rotation = 0.01;
        clearInterval(instance.spriteInterval);
      };

      const onDragStart = function(event: any) {
        this.data = event.data;
        dragging = true;
      };

      const dragEnd = function() {
        if (falling) {
          initGravity();
        }
        if (!mouseover) {
          spriteContainer.vx = prevVx;
        }
        sprite.texture = dance2Texture;
        dragging = false;

        // set the interaction data to null
        this.data = null;
        spriteContainer.cursor = 'pointer';
        initInterval();
      };

      const onDragMove = function() {
        if (dragging) {
          if (!falling) {
            beginDrag();
          }
          const newPosition = this.data.getLocalPosition(this.parent);
          forwardDelta = Math.abs(this.position.y - newPosition.y);

          this.position.x = newPosition.x;
          this.position.y = newPosition.y;
        }
      };

      spriteContainer
        .on('mousedown', onDragStart)
        .on('touchstart', onDragStart)
        .on('mouseup', dragEnd)
        .on('mouseupoutside', dragEnd)
        .on('touchend', dragEnd)
        .on('touchendoutside', dragEnd)
        .on('mousemove', onDragMove)
        .on('touchmove', onDragMove);

      const initInterval = function() {
        clearInterval(instance.spriteInterval);
        instance.spriteInterval = setInterval(() => {
          if (sprite.texture === dance1Texture) {
            sprite.texture = dance2Texture;
          } else {
            sprite.texture = dance1Texture;
          }
        }, 700);
      };

      initInterval();
    }

    function gameLoop() {
      const screenWidth = screen.width;
      const screenHeight = screen.height;

      switch (lastCollision) {
        case wall.bottom:
          if (spriteContainer.x + spriteSize + spriteSize / 8 > screenWidth) {
            lastCollision = wall.right;
            spriteContainer.vx = 0;
            spriteContainer.vy = -speed;
            spriteContainer.rotation = -1.5;
            instance.bubbles.rotation = -0.06;
          }
          break;
        case wall.right:
          if (spriteContainer.y - spriteSize - resolution - 1 <= 0) {
            lastCollision = wall.top;
            spriteContainer.vy = 0;
            spriteContainer.vx = -speed;
            spriteContainer.rotation = 3.1;
            instance.bubbles.rotation = 0.03;
          }
          break;
        case wall.top:
          if (spriteContainer.x - spriteSize - spriteSize / 6 <= 0) {
            lastCollision = wall.left;
            spriteContainer.vx = 0;
            spriteContainer.vy = speed;
            spriteContainer.rotation = 1.7;
            instance.bubbles.rotation = -0.1;
          }
          break;
        case wall.left:
          if (
            spriteContainer.y + spriteSize + resolution >= screenHeight &&
            !dragging
          ) {
            clearInterval(gravityInterval);
            if (falling) {
              app.renderer.view.style.zIndex = '0';
              falling = false;
            }
            lastCollision = wall.bottom;
            spriteContainer.vx = speed;
            spriteContainer.vy = 0;
            spriteContainer.y = screenHeight - spriteSize + resolution;
            spriteContainer.rotation = 0;
            instance.bubbles.rotation = 0.01;
          }
          break;
      }

      spriteContainer.x += spriteContainer.vx;
      spriteContainer.y += spriteContainer.vy;
    }

    window.addEventListener('resize', this.handleResize);
    setTimeout(this.handleResize, 100);
  }

  componentDidUpdate(prevProps: IRenderProps, prevState: IState) {
    if (this.state.currentTrack !== prevState.currentTrack) {
      this.createBubbles(this.retrieveTrack(), true);
    }
  }

  nextTrack(): number {
    return this.state.currentTrack === this.props.data.length - 1
      ? 0
      : this.state.currentTrack + 1;
  }

  retrieveTrack(index: number = this.state.currentTrack): IWhistleTrack {
    return this.props.data[index].fields;
  }

  randomTrack(tracklist: WhistleTrackEntry = this.props.data): number {
    if (tracklist.length === 1) {
      return 0;
    }

    if (tracklist.length === 0) {
      throw new Error(`No whistle tracks available`);
    }

    return Math.floor(Math.random() * tracklist.length);
  }

  createBubbles(track: IWhistleTrack, visible = false) {
    const textString = `${track.name} — ${track.artist}`;

    const prevRot = this.bubbles ? this.bubbles.rotation : 0.01;
    if (this.bubbles) {
      this.spriteContainer.removeChild(this.bubbles);
    }

    const shadow = new filters.DropShadowFilter({
      quality: 10,
      alpha: 0.1,
      distance: 1,
      resolution: devicePixelRatio,
      rotation: 45,
      color: 0x666666,
    } as any);
    const fill = 0xededed;
    const container = new PIXI.Container();
    container.alpha = visible ? 1 : 0;
    this.bubbles = container;

    // small
    const small = new PIXI.Graphics();
    small.beginFill(fill);
    small.drawRoundedRect((spriteSize / devicePixelRatio) * 1.2, 25, 10, 10, 5);
    small.endFill();
    small.filters = [shadow];

    // med
    const med = new PIXI.Graphics();
    med.beginFill(fill);
    med.drawRoundedRect(0, -10, 40, 30, 17);
    med.endFill();
    med.filters = [shadow];

    // large w/ text
    const fontSize = 14;
    const width = (textString.length * fontSize) / 1.75;
    const large = new PIXI.Graphics();
    large.beginFill(fill);
    large.drawRoundedRect(-width + 40, -60, width, 40, 20);
    large.endFill();
    large.filters = [shadow];

    const textStyle = new PIXI.TextStyle({
      fontSize,
    });
    const text = new PIXI.Text(textString, textStyle);
    text.y = -60 / 2 - 18;
    text.x = -width + fontSize + 40;
    large.addChild(text);

    container.addChild(small);
    container.addChild(med);
    container.addChild(large);

    container.y = -spriteSize / devicePixelRatio;
    container.x = -spriteSize / devicePixelRatio;
    container.rotation = prevRot;

    this.spriteContainer.addChild(container);
  }

  componentWillUnmount() {
    clearInterval(this.spriteInterval);
    window.removeEventListener('resize', this.handleResize);
    PIXI.loader.reset();
    if (this.app) {
      this.app.destroy(true, true);
      this.app = null;
      this.container = undefined;
    }
  }

  handleResize = () => {
    if (this.app !== null) {
      this.app.renderer.resize(window.innerWidth, window.innerHeight);
    }
  };

  onAudioEnded = () => {
    if (this.props.data.length < 2) {
      return;
    }

    this.setState({ currentTrack: this.nextTrack() });
  };

  toggleSoundEnabled = () => {
    this.setState(({ soundEnabled }) => ({ soundEnabled: !soundEnabled }));
  };

  render() {
    const { soundEnabled } = this.state;
    return (
      <>
        <SoundControl
          src={soundEnabled ? SoundOn : SoundOff}
          onClick={this.toggleSoundEnabled}
        />

        <audio
          loop
          autoPlay
          src={this.retrieveTrack(this.state.currentTrack).file.fields.file.url}
          onEnded={this.onAudioEnded}
          muted={!this.state.soundEnabled}
        />
        <div ref={ref => (this.container = ref!)} />
      </>
    );
  }
}

export default () => <WhistleTrackRenderer render={DancingManDesktop} />;



// WEBPACK FOOTER //
// ./src/scenes/Home/DancingManDesktop.tsx